import RdnaSmartTable from '@ringdna/common/src/components/RdnaTable/RdnaSmartTable'
import React, { useEffect, useMemo, useState } from 'react'
import { Column } from 'react-table'
import { SFUser, useCreateUsersBatch } from '../../../../store/api/users-api'
import { COLUMN_WIDTH, getPaginationProps, License } from '../utils'
import RdnaText from '@ringdna/common/src/components/RdnaText'
import styled from 'styled-components'
import { serializeJsonIntoPlay } from '@ringdna/common/src/utils/serverUtils'
import { getErrorMessage } from '@ringdna/common/src/utils/errorUtils'
import { MessageEvent, PubNubChannels, usePubNub } from '../../../contexts/PubNubContext'
import RdnaProgressBar from '@ringdna/common/src/components/RdnaProgressBar'
import { Colors } from '@ringdna/common/src/constants/colors'

type Props = {
  accountId: number
  userList: SFUser[]
  areaCode: string | null
  extension: string | null
  locationId: number | null
  setDisableDone: (value: boolean) => void
  closeDrawer: () => void
}

type NewUser = {
  salesforceId: string
  userId: number
  name: string
  userDisplayName: string
  email: string
  title: string
  locationName: string
  extensionCode: string
  numberCreated: boolean
  numberDescription: string
  userCreated: boolean
  userDescription: string
  number: string
  numberId: number
}

export default function Success({
  accountId,
  userList,
  areaCode,
  extension,
  locationId,
  setDisableDone,
  closeDrawer
}: Props) {
  const [isSaving, setSaving] = useState<boolean>(true)
  const [percent, setPercent] = useState<number>(0)
  const [createdUsers, setCreatedUsers] = useState<NewUser[]>([])
  const createUsersBatch = useCreateUsersBatch()
  const { pubnub } = usePubNub()
  const source = userList.filter(
    user =>
      user.adminLicense ||
      user.dialerLicense ||
      user.calendarLicense ||
      user.gsLicense ||
      user.caiLicense ||
      user.momentsLicense ||
      user.revenueAILicense
  )

  useEffect(() => {
    const prepareUsers = source.map(user => ({
      ...user,
      areaCode: user.dialerLicense ? (areaCode ? areaCode : user.areaCode.length > 0 ? user.areaCode : null) : null,
      extensionCode: user.dialerLicense
        ? extension
          ? extension
          : user.extensionCode.length > 0
            ? user.extensionCode
            : null
        : null,
      locationId: user.dialerLicense
        ? locationId || (user.location && user.location.id != 1 ? user.location.id : null)
        : null
    }))

    isSaving &&
      createUsersBatch({
        body: serializeJsonIntoPlay({ salesforceUsers: [...prepareUsers], accountId: accountId })
      })
        .catch(error => toastr.error(getErrorMessage(error)))
        .finally(() => setSaving(false))
  }, [accountId, areaCode, createUsersBatch, extension, isSaving, locationId, source, userList])

  const showAlert = () => {
    const fewFailed = createdUsers.some(u => !u.userCreated)
    const allFailed = createdUsers.every(u => !u.userCreated)
    if (allFailed) {
      toastr.error('Users were not created')
    } else if (fewFailed) {
      toastr.warning('A few users were not created')
    } else {
      toastr.success('The create has completed.', 'User Create')
    }
  }

  useEffect(() => {
    const listener = {
      message: (e: MessageEvent) => {
        if (e.userMetadata === PubNubChannels.CREATE_SALESFORCE_USERS_STATUS) {
          setCreatedUsers([...createdUsers, e.message.message as NewUser])
          percent != 1 && setPercent(percent + 1 / source.length)
        }
        if (e.userMetadata === PubNubChannels.CREATE_SALESFORCE_USERS_FINISHED) {
          setPercent(1)
          showAlert()
          setDisableDone(false)
        }
      }
    }
    pubnub.addListener(listener)
    return () => {
      pubnub.removeListener(listener)
    }
  }, [createdUsers, percent, pubnub, setDisableDone, showAlert, source.length, userList.length])

  const userLicensesText = (newUser: NewUser) => {
    const user = source.find(u => u.salesforceId === newUser.salesforceId)
    if (!user) {
      return ''
    }
    const text = []
    user.dialerLicense && text.push(License.INTELLIGENT_DIALER)
    user.caiLicense && text.push(License.CONVERSATION_AI)
    user.momentsLicense && text.push(License.MOMENTS)
    user.gsLicense && text.push(License.GUIDED_SELLING)
    user.revenueAILicense && text.push(License.REVENUE_AI)
    user.calendarLicense && text.push(License.CALENDAR)
    user.adminLicense && text.push(License.ADMIN)
    return text.join(', ')
  }

  const columns = useMemo<Column<NewUser>[]>(
    () => [
      {
        Header: 'Name',
        accessor: user => user,
        disableSortBy: true,
        width: COLUMN_WIDTH,
        Cell: (data: { value: NewUser }) => {
          return (
            <RdnaText className={'break-column'} href={`#users/${data.value.userId}`} onClick={closeDrawer}>
              {data.value.userDisplayName}
            </RdnaText>
          )
        }
      },
      {
        Header: 'Email',
        accessor: 'email',
        disableSortBy: true,
        width: COLUMN_WIDTH,
        Cell: (data: { value: string }) => <RdnaText className={'break-column'}>{data.value}</RdnaText>
      },
      {
        Header: 'Smart Number',
        accessor: user => user,
        disableSortBy: true,
        width: COLUMN_WIDTH,
        Cell: (data: { value: NewUser }) => (
          <RdnaText className={'extendText'}>
            {data.value.numberCreated
              ? data.value.number
              : data.value.numberDescription === 'The number was not created.'
                ? '-'
                : data.value.numberDescription}
          </RdnaText>
        )
      },
      {
        Header: 'Location',
        accessor: 'locationName',
        disableSortBy: true,
        width: COLUMN_WIDTH,
        Cell: (data: { value: string }) => <RdnaText>{data.value || '-'}</RdnaText>
      },
      {
        Header: 'Licenses',
        accessor: user => user,
        disableSortBy: true,
        width: COLUMN_WIDTH,
        Cell: (data: { value: NewUser }) => (
          <RdnaText className={'extendText'}>
            {data.value.userCreated ? userLicensesText(data.value) : data.value.userDescription}
          </RdnaText>
        )
      }
    ],
    [userLicensesText]
  )

  return (
    <Container>
      {percent != 0 && percent != 1 && (
        <ProgressBar>
          <RdnaProgressBar percent={percent} color={Colors.M50} />
        </ProgressBar>
      )}
      <RdnaSmartTable
        isFetching={isSaving || percent == 0}
        columns={columns}
        data={createdUsers}
        {...getPaginationProps(createdUsers, createdUsers.length)}
      />
    </Container>
  )
}

const Container = styled.div`
  [role='cell'] {
    height: auto;
  }
  span:has(.extendText) {
    overflow: visible;
  }

  .extendText {
    white-space: break-spaces;
  }
`

const ProgressBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing * 2}px;
`
